import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { CONFIRM, CONFIRM_ACTION, LEAVE_TABS_MESSAGE } from '@app/constants';
import { usePrompt } from '@app/hooks';

import type dayjs from 'dayjs';

type FormValues =
  | string
  | number
  | null
  | undefined
  | boolean
  | dayjs.Dayjs
  | string[]
  | number[]
  | boolean[]
  | dayjs.Dayjs[]
  | undefined[]
  | null[]
  | Record<string, unknown>
  | Record<string, unknown>
  | Record<string, unknown>[];

export type FormType = Record<string, FormValues>;

type UseUnsavedDataOptions = {
  data?: FormType | null;
  skip?: boolean;
  title?: string;
  subtitle?: string;
  confirmText?: string;
  cancelText?: string;
};

export const useUnsavedData = ({
  data,
  skip = false,
  title = CONFIRM,
  subtitle = LEAVE_TABS_MESSAGE,
  confirmText = CONFIRM_ACTION,
  cancelText,
}: UseUnsavedDataOptions) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [savedData, setSavedData] = useState<FormType | null>(null);

  const emptyObject = useMemo(
    () =>
      data === undefined ||
      data === null ||
      Object.keys(data ?? {}).length === 0,
    [data]
  );

  const checkHasChanges = useCallback(() => {
    const unsavedForm = data;
    if (savedData !== null && unsavedForm && isInitialized) {
      const savedDataKeys = Object.keys(savedData);
      const unsavedFormKeys = Object.keys(unsavedForm);
      if (savedDataKeys.length === 0 || unsavedFormKeys.length === 0) {
        setHasChanges(false);
      }
      setHasChanges(!isEqual(savedData, unsavedForm));
    }
  }, [data, savedData, isInitialized]);

  const initDate = useCallback(() => {
    const savedForm = data;
    if (!savedForm) return;
    setSavedData(savedForm);
    setIsInitialized(true);
  }, [data]);

  const { confirm } = usePrompt({
    hasChanges: hasChanges && !skip && !emptyObject && isInitialized,
    title,
    subtitle,
    confirmText,
    cancelText,
  });

  const saveData = useCallback(() => {
    setIsInitialized(false);
    setSavedData(data ?? null);
    setTimeout(() => {
      setHasChanges(false);
    }, 10);
  }, [data]);

  const onCancelHandler = useCallback(
    (action?: () => void, cancel?: () => void) => async () => {
      const result = await confirm();
      if (result) {
        saveData();
        action?.();
      } else {
        cancel?.();
      }
    },
    [confirm, saveData]
  );

  useEffect(() => {
    if (skip || !hasChanges) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [skip, hasChanges]);

  useEffect(() => {
    checkHasChanges();
  }, [checkHasChanges]);

  useEffect(() => {
    if (!isInitialized && !skip && !emptyObject) {
      setTimeout(initDate, 100);
    }
  }, [skip, isInitialized, initDate, emptyObject]);

  return {
    onCancelHandler,
    saveData,
    hasChanges,
  };
};
